// Middle column: full Order detail with sections — Header, Client, Products,
// Shipping, Payment, Timeline, Notes, Tags. Modeled after Shopify Admin's order
// page but tighter.
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ExternalLink, Copy, Truck, FileText, Plus, Trash2,
  Package, MapPin, CreditCard, ShieldAlert, Tag as TagIcon,
  CheckCircle2, Circle, Clock, RefreshCw, Loader2, ShoppingBag,
} from "lucide-react";
import { fmtDate, fmtShort } from "@/lib/format";
import { addNote, deleteNote, setInternalTags, markFulfilled } from "@/lib/orders";

export default function OrderDetail({ data, onRefresh }) {
  const { order, notes, activity } = data;
  const fin = statusInfo(order.financial_status, "financial");
  const ful = statusInfo(order.fulfillment_status, "fulfillment");
  const cancelled = !!order.cancelled_at;
  const tracking = (order.tracking || [])[0];
  const [fulfilling, setFulfilling] = useState(false);
  const canFulfill = !cancelled && order.fulfillment_status !== "fulfilled";

  const doFulfill = async () => {
    if (!window.confirm(`Marchezi comanda ${order.name} ca expediată în Shopify?`)) return;
    setFulfilling(true);
    toast.loading("Marchez ca expediată...", { id: "fulfill" });
    try {
      const r = await markFulfilled(order.id);
      if (r.already) {
        toast.success("Comanda era deja expediată", { id: "fulfill" });
      } else if (r.ok) {
        toast.success(`Marcat ca expediat (${r.fulfilled_count} fulfilment)`, { id: "fulfill" });
      } else {
        toast.error(r.error || "Eroare la fulfilment", { id: "fulfill" });
      }
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare", { id: "fulfill" });
    } finally {
      setFulfilling(false);
    }
  };

  const copy = (text, label = "Copiat") => {
    navigator.clipboard.writeText(text || "");
    toast.success(label);
  };

  const shopifyAdminUrl = `https://ieftinit-2841.myshopify.com/admin/orders/${order.shopify_id}`;

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="order-detail">
      {/* HEADER */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-bold tracking-tight">{order.name}</h1>
              {cancelled ? (
                <Badge className="bg-red-500/10 text-red-600 border-red-500/30 hover:bg-red-500/10">
                  Anulată
                </Badge>
              ) : (
                <>
                  <StatusPill {...fin} />
                  <StatusPill {...ful} />
                </>
              )}
              {order.risk_level === "high" && (
                <Badge variant="outline" className="border-red-500/30 text-red-500">
                  <ShieldAlert className="h-3 w-3 mr-1" /> risc ridicat
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
              <span>{order.shopify_created_at ? fmtDate(order.shopify_created_at) : ""}</span>
              {order.source && <span className="capitalize">· {order.source}</span>}
              {order.payment_method && <span>· {order.payment_method}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-2xl font-bold tabular-nums">
              {Number(order.total).toFixed(2)} {order.currency}
            </div>
            <div className="text-xs text-muted-foreground">
              {order.items_count} produs{order.items_count !== 1 ? "e" : ""}
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap items-center gap-1.5 mt-4">
          {canFulfill && (
            <Button
              size="sm"
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={doFulfill}
              disabled={fulfilling}
              data-testid="order-mark-fulfilled-btn"
            >
              {fulfilling ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Marchează ca expediată
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-8" asChild>
            <a href={shopifyAdminUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Shopify
            </a>
          </Button>
          {order.phone && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => copy(order.phone, "Telefon copiat")}
                data-testid="order-copy-phone"
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copiază telefon
              </Button>
            </>
          )}
          {tracking?.url && (
            <Button variant="outline" size="sm" className="h-8" asChild>
              <a href={tracking.url} target="_blank" rel="noreferrer">
                <Truck className="h-3.5 w-3.5 mr-1.5" /> AWB
              </a>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={onRefresh}
            data-testid="order-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        {/* CLIENT */}
        <Section title="Client">
          <Card className="p-4 bg-card border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <KV label="Nume" value={order.customer_name} />
              <KV label="Email" value={order.email} mono />
              <KV label="Telefon" value={order.phone} mono />
              <KV label="Telefon normalizat" value={order.normalized_phone} mono />
            </div>
            {order.shipping_address && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-4 mb-2 flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" /> Adresă livrare
                </div>
                <div className="text-sm">
                  {[order.shipping_address.address1, order.shipping_address.address2,
                    order.shipping_address.city, order.shipping_address.province,
                    order.shipping_address.zip, order.shipping_address.country]
                    .filter(Boolean).join(", ")}
                </div>
              </>
            )}
          </Card>
        </Section>

        {/* PRODUCTS */}
        <Section title={`Produse (${order.items_count})`} icon={Package}>
          <div className="space-y-2">
            {(order.items || []).map((it, i) => (
              <Card key={i} className="p-3 bg-card border-border flex gap-3">
                {it.image ? (
                  <img
                    src={it.image}
                    alt={it.title}
                    className="h-12 w-12 rounded object-cover bg-secondary shrink-0"
                  />
                ) : (
                  <div className="h-12 w-12 rounded bg-secondary flex items-center justify-center shrink-0">
                    <Package className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.title}</div>
                  {it.variant_title && (
                    <div className="text-xs text-muted-foreground">{it.variant_title}</div>
                  )}
                  {it.sku && (
                    <div className="text-[10px] text-muted-foreground font-mono">SKU: {it.sku}</div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold tabular-nums">
                    {it.quantity} × {Number(it.price).toFixed(2)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    = {Number(it.total).toFixed(2)} {order.currency}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Section>

        {/* SHIPPING */}
        {tracking && (
          <Section title="Livrare" icon={Truck}>
            <Card className="p-4 bg-card border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <KV label="Curier" value={tracking.courier} />
                <KV label="AWB" value={tracking.awb} mono />
                <KV label="Status fulfilment" value={tracking.status?.toLowerCase()} />
                <KV
                  label="Livrată"
                  value={tracking.delivered_at ? fmtDate(tracking.delivered_at) : "—"}
                />
              </div>
              {tracking.url && (
                <Button variant="outline" size="sm" className="mt-3" asChild>
                  <a href={tracking.url} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Track package
                  </a>
                </Button>
              )}
            </Card>
          </Section>
        )}

        {/* PAYMENT */}
        <Section title="Plată" icon={CreditCard}>
          <Card className="p-4 bg-card border-border">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <KV label="Subtotal" value={`${Number(order.subtotal || 0).toFixed(2)} ${order.currency}`} />
              <KV label="TVA" value={`${Number(order.total_tax || 0).toFixed(2)} ${order.currency}`} />
              <KV label="Discount" value={`-${Number(order.total_discounts || 0).toFixed(2)} ${order.currency}`} />
              <KV
                label="TOTAL"
                value={`${Number(order.total).toFixed(2)} ${order.currency}`}
                highlight
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-3 pt-3 border-t border-border">
              <KV label="Metodă plată" value={order.payment_method} />
              <KV label="Status financiar" value={order.financial_status} />
            </div>
            {order.discount_codes?.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  Cupoane:
                </span>
                {order.discount_codes.map((c, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] font-mono">
                    {c}
                  </Badge>
                ))}
              </div>
            )}
          </Card>
        </Section>

        {/* TIMELINE */}
        {(activity && activity.length > 0) && (
          <Section title="Timeline">
            <Timeline events={activity} />
          </Section>
        )}

        {/* NOTES */}
        <Section title="Note interne" icon={FileText}>
          <NotesSection
            orderId={order.id}
            notes={notes || []}
            onChanged={onRefresh}
          />
        </Section>

        {/* TAGS */}
        <Section title="Tag-uri" icon={TagIcon}>
          <TagsSection
            orderId={order.id}
            shopifyTags={order.shopify_tags || []}
            internalTags={order.internal_tags || []}
            onChanged={onRefresh}
          />
        </Section>
      </div>
    </div>
  );
}

// ---- subcomponents ----
function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />}
        {title}
      </div>
      {children}
    </section>
  );
}

function KV({ label, value, mono, highlight }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">
        {label}
      </div>
      <div
        className={`text-sm mt-0.5 ${mono ? "font-mono" : ""} ${highlight ? "font-display font-bold text-base" : "font-medium"} ${value && value !== "—" ? "" : "text-muted-foreground"}`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function StatusPill({ label, color }) {
  if (!label) return null;
  return (
    <Badge
      variant="outline"
      className="text-[10px] capitalize"
      style={{ color, background: color + "12", borderColor: color + "30" }}
    >
      {label}
    </Badge>
  );
}

function Timeline({ events }) {
  return (
    <div className="space-y-2 relative pl-4 before:content-[''] before:absolute before:left-1 before:top-2 before:bottom-2 before:w-px before:bg-border">
      {events.map((e) => (
        <div key={e.id} className="relative">
          <div className="absolute -left-3.5 top-1.5">
            <TimelineDot type={e.type} />
          </div>
          <div className="bg-card border border-border rounded-md p-2.5 ml-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium">{e.text}</div>
              <div className="text-[10px] text-muted-foreground shrink-0">
                {fmtShort(e.created_at)}
              </div>
            </div>
            {e.actor_name && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                de {e.actor_name}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineDot({ type }) {
  const COLORS = {
    created: "#3b82f6",
    paid: "#10b981",
    fulfilled: "#10b981",
    awb: "#3b82f6",
    delivered: "#10b981",
    cancelled: "#ef4444",
    refunded: "#f59e0b",
    note: "#8b5cf6",
    tag: "#71717a",
    status: "#f59e0b",
    webhook: "#71717a",
  };
  const c = COLORS[type] || "#71717a";
  return (
    <div className="h-3 w-3 rounded-full ring-4 ring-background" style={{ background: c }} />
  );
}

function NotesSection({ orderId, notes, onChanged }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addNote(orderId, text.trim());
      setText("");
      onChanged?.();
      toast.success("Notă adăugată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergi această notă?")) return;
    try {
      await deleteNote(orderId, id);
      onChanged?.();
    } catch (e) {
      toast.error("Eroare");
    }
  };

  return (
    <Card className="p-4 bg-card border-border">
      <div className="flex gap-2 mb-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Adaugă o notă internă (vizibilă doar agenților)..."
          className="text-sm bg-secondary/30 resize-none"
          data-testid="order-note-input"
        />
        <Button
          onClick={submit}
          disabled={saving || !text.trim()}
          className="bg-primary text-primary-foreground self-stretch"
          data-testid="order-add-note-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
      {notes.length === 0 ? (
        <div className="text-xs text-muted-foreground">Nicio notă internă încă.</div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="bg-secondary/30 rounded-md p-2.5 text-sm flex gap-2 group">
              <div className="flex-1 min-w-0">
                <div className="whitespace-pre-wrap">{n.text}</div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {n.author_name || "—"} · {fmtShort(n.created_at)}
                </div>
              </div>
              <button
                onClick={() => remove(n.id)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition"
                data-testid={`order-delete-note-${n.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function TagsSection({ orderId, shopifyTags, internalTags, onChanged }) {
  const [tags, setTags] = useState(internalTags);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  const add = () => {
    const t = input.trim();
    if (!t) return;
    if (!tags.includes(t)) setTags([...tags, t]);
    setInput("");
  };

  const remove = (t) => setTags(tags.filter((x) => x !== t));

  const save = async () => {
    setSaving(true);
    try {
      await setInternalTags(orderId, tags);
      onChanged?.();
      toast.success("Tag-uri salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 bg-card border-border space-y-3">
      {shopifyTags.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5 flex items-center gap-1.5">
            <ShoppingBag className="h-3 w-3" /> Tag-uri Shopify
          </div>
          <div className="flex flex-wrap gap-1.5">
            {shopifyTags.map((t, i) => (
              <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5 flex items-center gap-1.5">
          <TagIcon className="h-3 w-3" /> Tag-uri interne (operatori)
        </div>
        <div className="flex gap-2 mb-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder="Ex: VIP, urgent, follow-up..."
            className="h-8 text-sm"
            data-testid="order-tag-input"
          />
          <Button variant="outline" onClick={add} size="sm" data-testid="order-add-tag-btn">
            Adaugă
          </Button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {tags.map((t, i) => (
              <Badge
                key={i}
                className="text-[10px] cursor-pointer hover:bg-red-500/20"
                onClick={() => remove(t)}
              >
                {t} ×
              </Badge>
            ))}
          </div>
        )}
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="bg-primary text-primary-foreground"
          data-testid="order-save-tags-btn"
        >
          {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          Salvează tag-uri
        </Button>
      </div>
    </Card>
  );
}

function statusInfo(s, kind) {
  if (!s) return { label: kind === "fulfillment" ? "neexpediată" : null, color: "#71717a" };
  const m = String(s).toLowerCase();
  if (m === "paid" || m === "partially_paid") return { label: "plătită", color: "#10b981" };
  if (m === "pending") return { label: "pending", color: "#f59e0b" };
  if (m === "refunded" || m === "partially_refunded") return { label: "refund", color: "#3b82f6" };
  if (m === "voided") return { label: "voided", color: "#71717a" };
  if (m === "fulfilled") return { label: "expediată", color: "#10b981" };
  if (m === "unfulfilled") return { label: "neexpediată", color: "#f59e0b" };
  if (m === "partial") return { label: "parțial", color: "#f59e0b" };
  return { label: m, color: "#71717a" };
}
